import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiCashMultiple, mdiPlus, mdiPencil, mdiTrashCan } from "@mdi/js";
import { toast } from "react-toastify";
import AppHeader from "../../components/AppHeader";
import { classApi } from "../../api/classes";
import { organizationApi } from "../../api/organization";
import { useAuth } from "../../contexts/AuthContext";
import CustomFormInput from "../../components/forms/CustomFormInput";
import ConfirmationModal from "../../components/ConfirmationModal";
import { formatCurrencyFromMinor } from "../../utils/currency";
import "../Common.css";
import "../Dashboard.css";
import "../header.css";
import "./ClassPricingPage.css";
const INSTALMENT_FREQUENCIES = [
    { value: "weekly", label: "Weekly instalments — pay per week" },
    { value: "fortnightly", label: "Fortnightly instalments" },
    { value: "monthly", label: "Monthly instalments" },
];

// Fee types — what kind of fee this pricing option represents
const FEE_TYPES = [
    { value: "term-class", label: "Term fee" },
    { value: "short-course", label: "Short course fee" },
    { value: "drop-in", label: "Drop-in fee" },
];

const emptyForm = {
    name: "",
    price: "",
    currency: "AUD",
    covers: "all",
    maxClasses: "",
    unlimitedClasses: true,
    termClassPayment: "once", // 'once' | 'weekly' | 'fortnightly' | 'monthly'
    shortCoursePayment: "once", // 'once' | 'weekly' | 'fortnightly' | 'monthly'
    instalments: "",
    feeType: "term-class",
    isActive: true,
};

/**
 * ClassPricingPage - Sub-Portal Feature for managing class pricing options
 * Portal type: Sub-Portal Feature (.sub-portal-header-card)
 */
const ClassPricingPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const orgId = user?.orgId || "";

    const [pricingOptions, setPricingOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingOption, setEditingOption] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [isSaving, setIsSaving] = useState(false);
    const [confirmModal, setConfirmModal] = useState(null); // { title, message, variant, onConfirm }

    // --- Refunds setting (school money policy — gates all refund UI) ---
    const [offersRefunds, setOffersRefunds] = useState(false);
    const [isLoadingRefunds, setIsLoadingRefunds] = useState(true);
    const [isSavingRefunds, setIsSavingRefunds] = useState(false);

    const loadRefundsSetting = useCallback(async () => {
        if (!orgId) {
            setIsLoadingRefunds(false);
            return;
        }
        setIsLoadingRefunds(true);
        try {
            const res = await organizationApi.getSettings(orgId);
            // getSettings returns the settings object directly (response.data.settings)
            setOffersRefunds(res?.offersRefunds === true);
        } catch {
            toast.error("Failed to load refund setting");
        } finally {
            setIsLoadingRefunds(false);
        }
    }, [orgId]);

    const handleToggleOffersRefunds = async next => {
        setIsSavingRefunds(true);
        try {
            const res = await organizationApi.updateOffersRefunds(orgId, next);
            setOffersRefunds(res?.offersRefunds === true);
            toast.success(next ? "Class refunds enabled" : "Class refunds disabled");
        } catch (err) {
            toast.error(err.message || "Failed to update refund setting");
        } finally {
            setIsSavingRefunds(false);
        }
    };

    const loadPricingOptions = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await classApi.getPricingOptions();
            if (response.ok) {
                setPricingOptions(response.data.pricingOptions || []);
            } else {
                toast.error(response.data?.message || "Failed to load pricing packages");
            }
        } catch {
            toast.error("Failed to load pricing packages");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPricingOptions();
        loadRefundsSetting();
    }, [loadPricingOptions, loadRefundsSetting]);

    const openCreate = () => {
        setEditingOption(null);
        setForm(emptyForm);
        setShowModal(true);
    };

    const openEdit = opt => {
        setEditingOption(opt);
        const feeType = opt.classType || "term-class";
        const isTermClass = feeType === "term-class";
        const isShortCourse = feeType === "short-course";
        setForm({
            name: opt.name || "",
            price: opt.price !== undefined ? String(opt.price / 100) : "",
            currency: opt.currency || "AUD",
            covers: opt.covers || "all",
            maxClasses: opt.maxClasses !== null && opt.maxClasses !== undefined ? String(opt.maxClasses) : "",
            unlimitedClasses: opt.maxClasses === null || opt.maxClasses === undefined,
            termClassPayment: isTermClass ? opt.paymentType || "once" : "once",
            shortCoursePayment: isShortCourse ? opt.paymentType || "once" : "once",
            instalments: opt.instalments ? String(opt.instalments) : "",
            feeType,
            isActive: opt.isActive !== false,
        });
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!form.name.trim()) {
            toast.error("Name is required");
            return;
        }
        if (!form.price || isNaN(parseFloat(form.price))) {
            toast.error("Valid price is required");
            return;
        }

        setIsSaving(true);
        try {
            const isTermClass = form.feeType === "term-class";
            const isShortCourse = form.feeType === "short-course";
            let paymentType = "once";
            if (isTermClass) paymentType = form.termClassPayment;
            else if (isShortCourse) paymentType = form.shortCoursePayment;

            const isInstalment = (isTermClass || isShortCourse) && paymentType !== "once";
            const price = Math.round(parseFloat(form.price) * 100);
            const instalments = isInstalment && form.instalments ? parseInt(form.instalments) : null;

            const payload = {
                name: form.name.trim(),
                price,
                currency: form.currency,
                covers: form.covers.trim() || "all",
                maxClasses: isShortCourse ? null : form.unlimitedClasses ? null : parseInt(form.maxClasses) || null,
                paymentType,
                paymentFrequency: isInstalment ? paymentType : null,
                instalments,
                classType: form.feeType,
                isActive: form.isActive,
            };

            let response;
            if (editingOption) {
                response = await classApi.updatePricingOption(editingOption.id, payload);
            } else {
                response = await classApi.createPricingOption(payload);
            }

            if (response.ok) {
                toast.success(editingOption ? "Pricing package updated" : "Pricing package created");
                setShowModal(false);
                loadPricingOptions();
            } else {
                toast.error(response.data?.message || "Failed to save");
            }
        } catch {
            toast.error("Failed to save pricing package");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = opt => {
        setConfirmModal({
            title: "Delete Pricing Package",
            message: `Delete "${opt.name}"? This cannot be undone.`,
            variant: "danger",
            onConfirm: async () => {
                setConfirmModal(null);
                try {
                    const response = await classApi.deletePricingOption(opt.id);
                    if (response.ok) {
                        toast.success("Pricing package deleted");
                        loadPricingOptions();
                    } else {
                        toast.error(response.data?.message || "Failed to delete");
                    }
                } catch {
                    toast.error("Failed to delete pricing package");
                }
            },
        });
    };

    const toggleActive = async opt => {
        try {
            const response = await classApi.updatePricingOption(opt.id, { isActive: !opt.isActive });
            if (response.ok) {
                loadPricingOptions();
            } else {
                toast.error("Failed to update");
            }
        } catch {
            toast.error("Failed to update");
        }
    };

    const formatPrice = cents => formatCurrencyFromMinor(cents);

    // Group options by fee type in display order
    const grouped = useMemo(() => {
        const order = ["term-class", "short-course", "drop-in"];
        const groups = {};
        for (const opt of pricingOptions) {
            const key = opt.classType || "term-class";
            if (!groups[key]) groups[key] = [];
            groups[key].push(opt);
        }
        return order.filter(k => groups[k]?.length > 0).map(k => ({ feeType: k, options: groups[k] }));
    }, [pricingOptions]);

    return (
        <div className="dashboard-container org-console">
            <AppHeader />

            <div className="dashboard-main">
                <div className="dashboard-content-wrapper">
                    <div className="sub-portal-header-card">
                        <div className="sub-portal-header-title-row">
                            <button className="back-nav-btn" onClick={() => navigate("/classes/manage")}>
                                ← Manage Classes
                            </button>
                            <h1 className="sub-portal-header-title">
                                <Icon
                                    path={mdiCashMultiple}
                                    size={1.1}
                                    color="#451f78"
                                    style={{ marginRight: 8, verticalAlign: "middle" }}
                                />
                                Pricing Packages
                            </h1>
                        </div>
                        <p className="sub-portal-header-subtitle">
                            Configure pricing packages for your classes. Students pay based on the best matching
                            package.
                        </p>
                    </div>

                    <div className="content-card class-pricing-card">
                        {/* Transaction Settings — pricing-related policy, above the packages table */}
                        <div className="class-pricing-actions-bar">
                            <h3>Transaction Settings</h3>
                        </div>
                        {isLoadingRefunds ? (
                            <p className="class-pricing-loading">Loading refund setting...</p>
                        ) : (
                            <>
                                <p className="class-pricing-refunds-hint">
                                    When Unchecked, no refund option will appear anywhere — removing a student or
                                    cancelling a class returns no money. Check this box only if your school issues money
                                    refunds; you can then issue a full or partial refund when removing a student or
                                    cancelling a class or session.
                                </p>
                                <CustomFormInput
                                    type="checkbox"
                                    variant="turquoise"
                                    label="This school offers class refunds"
                                    checked={offersRefunds}
                                    disabled={isSavingRefunds}
                                    onChange={e => handleToggleOffersRefunds(e.target.checked)}
                                />
                            </>
                        )}

                        <div className="class-pricing-section-divider" />

                        <div className="class-pricing-actions-bar">
                            <h3>Pricing Packages</h3>
                            <button className="class-pricing-add-btn" onClick={openCreate}>
                                <Icon path={mdiPlus} size={0.9} /> Add Package
                            </button>
                        </div>

                        {isLoading ? (
                            <p className="class-pricing-loading">Loading pricing packages...</p>
                        ) : pricingOptions.length === 0 ? (
                            <div className="class-pricing-empty">
                                <Icon path={mdiCashMultiple} size={2} color="#ccc" />
                                <p>No pricing packages yet. Add your first package to get started.</p>
                                <button className="class-pricing-add-btn" onClick={openCreate}>
                                    <Icon path={mdiPlus} size={0.85} /> Add Pricing Package
                                </button>
                            </div>
                        ) : (
                            <table className="class-pricing-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Price</th>
                                        <th>Covers</th>
                                        <th>Max Classes</th>
                                        <th>Active</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                {grouped.map(({ feeType, options }) => {
                                    const groupLabel = FEE_TYPES.find(t => t.value === feeType)?.label || feeType;
                                    const showHeader = grouped.length > 1;
                                    return (
                                        <tbody key={feeType}>
                                            {showHeader && (
                                                <tr className="class-pricing-group-row">
                                                    <td colSpan={6} className="class-pricing-group-header-cell">
                                                        {groupLabel}
                                                    </td>
                                                </tr>
                                            )}
                                            {options.map(opt => (
                                                <tr
                                                    key={opt.id}
                                                    className={opt.isActive ? "" : "class-pricing-row-inactive"}>
                                                    <td>
                                                        <span className="class-pricing-name">{opt.name}</span>
                                                        {opt.instalments > 1 && (
                                                            <span className="class-pricing-recurring-note">
                                                                {opt.paymentFrequency || opt.paymentType} ×{" "}
                                                                {opt.instalments} payments
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>{formatPrice(opt.price)}</td>
                                                    <td>{opt.covers}</td>
                                                    <td>{opt.maxClasses !== null ? opt.maxClasses : "∞"}</td>
                                                    <td>
                                                        <label className="dashboard-toggle-ios">
                                                            <input
                                                                type="checkbox"
                                                                checked={opt.isActive}
                                                                onChange={() => toggleActive(opt)}
                                                            />
                                                            <span className="toggle-slider"></span>
                                                        </label>
                                                    </td>
                                                    <td>
                                                        <div className="class-pricing-actions">
                                                            <button
                                                                className="class-pricing-action-btn"
                                                                onClick={() => openEdit(opt)}
                                                                title="Edit">
                                                                <Icon path={mdiPencil} size={0.8} />
                                                            </button>
                                                            <button
                                                                className="class-pricing-action-btn danger"
                                                                onClick={() => handleDelete(opt)}
                                                                title="Delete">
                                                                <Icon path={mdiTrashCan} size={0.8} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    );
                                })}
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {showModal && (
                <div className="class-pricing-modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="class-pricing-modal" onClick={e => e.stopPropagation()}>
                        <div className="class-pricing-modal-header">
                            <div className="class-pricing-modal-header-content">
                                <Icon path={mdiCashMultiple} size={1} color="#451f78" />
                                <h2>{editingOption ? "Edit Pricing Package" : "New Pricing Package"}</h2>
                            </div>
                            <button className="class-pricing-modal-close" onClick={() => setShowModal(false)}>
                                ×
                            </button>
                        </div>

                        <div className="class-pricing-modal-body">
                            <div className="class-pricing-form">
                                <label>Name *</label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                    placeholder="e.g. L2+ Two Classes, Green Membership"
                                />

                                <label>Price (AUD)</label>
                                <p className="form-hint">Enter the amount in dollars (e.g. 150 for $150.00)</p>
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={form.price}
                                    onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                                    placeholder="150.00"
                                />

                                <label>Covers (level/tier)</label>
                                <p className="form-hint">
                                    Enter a level label matching your class levels, e.g. "beginner", "L2+", or "all"
                                </p>
                                <input
                                    type="text"
                                    value={form.covers}
                                    onChange={e => setForm(f => ({ ...f, covers: e.target.value }))}
                                    placeholder="all"
                                />

                                {form.feeType !== "short-course" && (
                                    <>
                                        <label>Maximum Classes</label>
                                        <CustomFormInput
                                            type="checkbox"
                                            variant="turquoise"
                                            label="Unlimited"
                                            checked={form.unlimitedClasses}
                                            onChange={e => setForm(f => ({ ...f, unlimitedClasses: e.target.checked }))}
                                        />
                                        {!form.unlimitedClasses && (
                                            <input
                                                type="number"
                                                min="1"
                                                value={form.maxClasses}
                                                onChange={e => setForm(f => ({ ...f, maxClasses: e.target.value }))}
                                                placeholder="2"
                                            />
                                        )}
                                    </>
                                )}

                                <label>Fee Type</label>
                                <p className="form-hint">What kind of payment is this option for?</p>
                                <select
                                    value={form.feeType}
                                    onChange={e => setForm(f => ({ ...f, feeType: e.target.value }))}>
                                    {FEE_TYPES.map(t => (
                                        <option key={t.value} value={t.value}>
                                            {t.label}
                                        </option>
                                    ))}
                                </select>

                                {form.feeType === "term-class" && (
                                    <>
                                        <label>Payment Structure</label>
                                        <p className="form-hint">How does the student pay for this term?</p>
                                        <select
                                            value={form.termClassPayment}
                                            onChange={e => setForm(f => ({ ...f, termClassPayment: e.target.value }))}>
                                            <option value="once">Full term — single payment</option>
                                            {INSTALMENT_FREQUENCIES.map(t => (
                                                <option key={t.value} value={t.value}>
                                                    {t.label}
                                                </option>
                                            ))}
                                        </select>
                                        {form.termClassPayment !== "once" && (
                                            <>
                                                <label>Number of payments</label>
                                                <p className="form-hint">
                                                    To set up a membership with weekly payments — e.g. $55/week for a
                                                    10-week term — select Weekly instalments and enter 10 as the number
                                                    of payments. The price field is the per-payment amount.
                                                </p>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={form.instalments}
                                                    onChange={e =>
                                                        setForm(f => ({ ...f, instalments: e.target.value }))
                                                    }
                                                    placeholder="e.g. 10"
                                                />
                                            </>
                                        )}
                                    </>
                                )}

                                {form.feeType === "short-course" && (
                                    <>
                                        <label>Payment Structure</label>
                                        <p className="form-hint">How does the student pay for this short course?</p>
                                        <select
                                            value={form.shortCoursePayment}
                                            onChange={e =>
                                                setForm(f => ({ ...f, shortCoursePayment: e.target.value }))
                                            }>
                                            <option value="once">Full course — single payment</option>
                                            {INSTALMENT_FREQUENCIES.map(t => (
                                                <option key={t.value} value={t.value}>
                                                    {t.label}
                                                </option>
                                            ))}
                                        </select>
                                        {form.shortCoursePayment !== "once" && (
                                            <>
                                                <label>Number of payments</label>
                                                <p className="form-hint">
                                                    To collect a short course fee week-by-week — e.g. $50/week for 3
                                                    weeks — select Weekly instalments and enter 3 as the number of
                                                    payments. The price field is the per-week amount.
                                                </p>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={form.instalments}
                                                    onChange={e =>
                                                        setForm(f => ({ ...f, instalments: e.target.value }))
                                                    }
                                                    placeholder="e.g. 3"
                                                />
                                            </>
                                        )}
                                    </>
                                )}

                                <CustomFormInput
                                    type="checkbox"
                                    variant="turquoise"
                                    label="Active (visible in payment flow)"
                                    checked={form.isActive}
                                    onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                                />
                            </div>
                        </div>

                        <div className="class-pricing-modal-footer">
                            <button className="class-pricing-modal-cancel-btn" onClick={() => setShowModal(false)}>
                                Cancel
                            </button>
                            <button className="class-pricing-modal-save-btn" onClick={handleSave} disabled={isSaving}>
                                {isSaving ? "Saving..." : "Save Option"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {confirmModal && (
                <ConfirmationModal
                    isOpen
                    title={confirmModal.title}
                    message={confirmModal.message}
                    variant={confirmModal.variant}
                    onConfirm={confirmModal.onConfirm}
                    onCancel={() => setConfirmModal(null)}
                />
            )}
        </div>
    );
};

export default ClassPricingPage;
